import { Link, Upload } from "@mui/icons-material";
import {
  Button,
  Paper,
  Popper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import useId from "@mui/material/utils/useId";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import React, { useEffect, useRef, useState } from "react";
import { isUrl } from "../../../util/validation";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles({
  popover: {
    zIndex: 100,
  },
});

export type DocumentationInputValue =
  | {
      type: "file";
      fileName: string;
      fileKey?: string;
      file?: File;
    }
  | {
      type: "url";
      url: string;
    };

export type DocumentationErrorValue = {
  type: "file" | "url";
  error: string;
};

export type UseDocumentationInputProps = {
  initialValue?: DocumentationInputValue;
  onChange?: (
    value: DocumentationInputValue | null,
    prevValue: DocumentationInputValue | null,
  ) => Promise<void>;
};

export const useDocumentationInput = ({ initialValue, onChange }: UseDocumentationInputProps) => {
  const [internalValue, setInternalValue] = useState<DocumentationInputValue | null>(initialValue);
  const [value, setValue] = useState<DocumentationInputValue | null>(null);

  useEffect(() => {
    setInternalValue(initialValue);
    setValue(initialValue);
  }, [initialValue]);

  const [error, setError] = useState<DocumentationErrorValue | null>(null);
  const [loading, setLoading] = useState(false);

  const clear = async () => {
    setLoading(true);
    setError(null);
    setInternalValue(null);
    if (onChange) {
      try {
        await onChange(null, value);
      } catch (e) {
        setError({
          type: value?.type,
          error: e.message,
        });
      }
    }
    setValue(null);
    setLoading(false);
  };

  const save = async (newValue: DocumentationInputValue | null) => {
    setLoading(true);
    setError(null);
    if (onChange) {
      try {
        await onChange(newValue, value);
      } catch (e) {
        setError({
          type: newValue.type,
          error: e.message,
        });
      }
    }
    setValue(newValue);
    setLoading(false);
  };

  return {
    value,
    clear,
    loading,
    error,
    props: {
      error,
      setError,
      internalValue,
      value,
      onDelete: clear,
      setUrl: async (url: string, requireExplicitSave = false) => {
        if (internalValue?.type != "url" || internalValue?.url != url) {
          const newValue: DocumentationInputValue = {
            type: "url",
            url,
          };
          setError(null);
          setInternalValue(newValue);
          if (!requireExplicitSave) {
            await save(newValue);
          }
        }
      },
      save: () => save(internalValue ? { ...internalValue } : null),
      setFile: async (file: File) => {
        const newValue: DocumentationInputValue = {
          type: "file",
          fileName: file.name,
          file,
        };
        setError(null);
        await save(newValue);
        setInternalValue(newValue);
      },
    },
  };
};

export const DocumentationInput: React.FC<{
  internalValue?: DocumentationInputValue;
  value?: DocumentationInputValue;
  error?: DocumentationErrorValue;
  setError: (error: DocumentationErrorValue | null) => void;
  variant?: "inline" | "popover";
  disabled?: boolean;
  onDelete: () => void;
  setUrl: (url: string, requireExplicitSave?: boolean) => void;
  setFile: (file: File) => Promise<void>;
  save: () => void;
}> = ({ internalValue, value, error, variant, disabled, setUrl, setFile, save, setError }) => {
  const [popoverTarget, setPopoverTarget] = React.useState<null | HTMLElement>(null);
  const id = useId();
  const uploadEl = useRef<HTMLInputElement>();
  const [showPopover, setShowPopover] = useState(false);
  const classes = useStyles();

  let fileType = "";
  if (value?.type == "url" && (value.url || variant == "inline")) {
    fileType = "url";
  } else if (value?.type == "file") {
    fileType = "file";
  }

  return (
    <div className="document-input d-flex flex-row align-items-center">
      <input
        style={{ display: "none" }}
        ref={uploadEl}
        id={`note-upload-${id}`}
        type="file"
        multiple={false}
        onChange={async (event) => {
          const file = event.target.files[0];
          if (file && file.size <= 15000000) {
            await setFile(file);
          } else {
            setError({ type: "file", error: "File size must be under 15MB." });
          }
        }}
      />

      <ToggleButtonGroup
        size="small"
        value={fileType}
        onChange={(_e, newType) => {
          if (newType == "file") {
            setShowPopover(false);
            uploadEl.current?.click();
            // Open file thing
          } else if (newType == "url") {
            setShowPopover(true);
            if (newType != fileType) {
              setUrl("");
            }
          } else if (variant != "inline" && value?.type == "url") {
            setShowPopover(!showPopover);
          }
        }}
        exclusive
        className={`${variant == "inline" ? "mr-sm" : ""}`}
        disabled={disabled}
      >
        <ToggleButton
          disabled={disabled}
          value="url"
          className="p-relative"
          onClick={(event) => {
            setPopoverTarget(event.currentTarget);
          }}
        >
          <Tooltip title="Set a Link">
            <Link />
          </Tooltip>
        </ToggleButton>

        <ToggleButton disabled={disabled} value="file">
          <Tooltip title="Upload a File">
            <Upload />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>

      {variant != "inline" && (
        <Popper
          open={showPopover}
          anchorEl={popoverTarget}
          placement="bottom-end"
          className={classes.popover}
        >
          <ClickAwayListener
            onClickAway={() => {
              setShowPopover(false);
            }}
          >
            <Paper elevation={4}>
              <div className="p-xs pt-sm w-256">
                <div className="pb-xs">
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={internalValue?.type == "url" ? internalValue.url : ""}
                    error={Boolean(error)}
                    disabled={disabled}
                    onChange={(e) => setUrl(e.target.value, true)}
                    onBlur={(e) => setUrl(e.target.value, true)}
                    onKeyUp={(e) => {
                      if (e.keyCode === 13) {
                        e.preventDefault();
                        if (internalValue?.type == "url" && isUrl(internalValue.url)) {
                          save();
                          setShowPopover(false);
                        } else {
                          setError({ type: "url", error: "Please provide a valid URL." });
                        }
                      }
                    }}
                    placeholder="http://example.com/path-to-documentation"
                    InputProps={{ type: "text" }}
                    InputLabelProps={{ required: false }}
                    label="URL to document"
                    required
                  />
                </div>
                {Boolean(error) && (
                  <p className="color-red-600 m-0 p-0 mb-xs text-t1">{error.error}</p>
                )}
                <div>
                  <Button
                    size="small"
                    variant="text"
                    color="secondary"
                    className="fill justify-space-between"
                    onClick={(e) => {
                      e.preventDefault();
                      if (internalValue?.type == "url" && isUrl(internalValue.url)) {
                        save();
                        setShowPopover(false);
                      } else {
                        setError({ type: "url", error: "Please provide a valid URL." });
                      }
                    }}
                  >
                    <span>Add link</span>
                    <Link />
                  </Button>
                </div>
              </div>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}

      {variant == "inline" && value?.type == "url" && (
        <>
          <TextField
            size="small"
            variant="outlined"
            value={internalValue?.type == "url" ? internalValue.url : ""}
            error={Boolean(error)}
            className="w-100"
            disabled={disabled || value?.type != "url"}
            onChange={(e) => setUrl(e.target.value, true)}
            onBlur={async (e) => {
              setUrl(e.target.value);
              save();
            }}
            onKeyUp={(e) => {
              if (e.keyCode === 13) {
                e.preventDefault();
                if (internalValue?.type == "url" && isUrl(internalValue.url)) {
                  save();
                } else {
                  setError({ type: "url", error: "Please provide a valid URL." });
                }
              }
            }}
            placeholder="http://example.com/path-to-documentation"
            InputProps={{ type: "text" }}
            InputLabelProps={{ required: false }}
            label="URL to document"
            required
          />
        </>
      )}
    </div>
  );
};
